// components/MathContent.jsx
import { useEffect, useRef } from "react";

// ===== لیست دستورات LaTeX =====
const LATEX_COMMANDS = [
  "dfrac",
  "frac",
  "tfrac",
  "sqrt",
  "cbrt",
  "sum",
  "prod",
  "int",
  "iint",
  "iiint",
  "oint",
  "lim",
  "limsup",
  "liminf",
  "bigcup",
  "bigcap",
  "bigvee",
  "bigwedge",
  "bigoplus",
  "bigotimes",
  "bigodot",
  "alpha",
  "beta",
  "gamma",
  "delta",
  "epsilon",
  "varepsilon",
  "zeta",
  "eta",
  "theta",
  "vartheta",
  "iota",
  "kappa",
  "lambda",
  "mu",
  "nu",
  "xi",
  "pi",
  "varpi",
  "rho",
  "varrho",
  "sigma",
  "varsigma",
  "tau",
  "upsilon",
  "phi",
  "varphi",
  "chi",
  "psi",
  "omega",
  "Gamma",
  "Delta",
  "Theta",
  "Lambda",
  "Xi",
  "Pi",
  "Sigma",
  "Upsilon",
  "Phi",
  "Psi",
  "Omega",
  "times",
  "div",
  "pm",
  "mp",
  "cdot",
  "ast",
  "star",
  "circ",
  "bullet",
  "leq",
  "geq",
  "neq",
  "approx",
  "equiv",
  "sim",
  "simeq",
  "cong",
  "propto",
  "subset",
  "supset",
  "subseteq",
  "supseteq",
  "in",
  "notin",
  "cup",
  "cap",
  "setminus",
  "emptyset",
  "forall",
  "exists",
  "nexists",
  "neg",
  "land",
  "lor",
  "implies",
  "iff",
  "to",
  "rightarrow",
  "leftarrow",
  "leftrightarrow",
  "Rightarrow",
  "Leftarrow",
  "Leftrightarrow",
  "mapsto",
  "longmapsto",
  "longrightarrow",
  "longleftarrow",
  "infty",
  "partial",
  "nabla",
  "ell",
  "hbar",
  "Re",
  "Im",
  "wp",
  "sin",
  "cos",
  "tan",
  "cot",
  "sec",
  "csc",
  "arcsin",
  "arccos",
  "arctan",
  "sinh",
  "cosh",
  "tanh",
  "coth",
  "log",
  "ln",
  "lg",
  "exp",
  "det",
  "dim",
  "ker",
  "deg",
  "gcd",
  "hom",
  "arg",
  "min",
  "max",
  "sup",
  "inf",
  "vec",
  "hat",
  "bar",
  "tilde",
  "dot",
  "ddot",
  "overline",
  "underline",
  "widehat",
  "widetilde",
  "overbrace",
  "underbrace",
  "overset",
  "underset",
  "left",
  "right",
  "langle",
  "rangle",
  "lceil",
  "rceil",
  "lfloor",
  "rfloor",
  "lbrace",
  "rbrace",
  "lbrack",
  "rbrack",
  "begin",
  "end",
  "matrix",
  "pmatrix",
  "bmatrix",
  "vmatrix",
  "cases",
  "displaystyle",
  "textstyle",
  "scriptstyle",
  "scriptscriptstyle",
  "quad",
  "qquad",
  "hspace",
  "vspace",
  "phantom",
  "hphantom",
  "vphantom",
  "text",
  "mathrm",
  "mathbf",
  "mathit",
  "mathsf",
  "mathtt",
  "mathcal",
  "mathbb",
  "mathfrak",
  "boldsymbol",
  "operatorname",
  "overrightarrow",
  "overleftarrow",
];

// ===== ساخت regex برای دستورات LaTeX =====
// نکته: «ضریب صحیح» اختیاری (مثل ۲ در ۲\frac{۳}{۸}) و فرمان‌های فاصله
//       (\, \; \: \!) هم باید در همان $...$ قرار بگیرند تا در RTL جابه‌جا نشوند.
const buildLatexRegex = () => {
  const sortedCommands = [...LATEX_COMMANDS].sort(
    (a, b) => b.length - a.length,
  );
  const commandGroup = sortedCommands.join("|");
  const argsPattern = `(?:\\{[^{}]*(?:\\{[^{}]*\\}[^{}]*)*\\})*`;
  const subscript = `(?:_\\{[^{}]*(?:\\{[^{}]*\\}[^{}]*)*\\})?`;
  const superscript = `(?:\\^\\{[^{}]*(?:\\{[^{}]*\\}[^{}]*)*\\})?`;

  // عدد صحیح یا اعشاری اختیاری + صفر یا چند فرمان فاصله مثل \, و \;
  // در template literal: \\\\ → \\ در string → \\ در regex (بک‌اسلش تحت‌اللفظی)
  const leadingCoeff = `(?:\\d+(?:[.,]\\d+)?(?:\\s*\\\\[,;:!]\\s*)*)?`;

  return new RegExp(
    `(${leadingCoeff}\\\\(${commandGroup})${argsPattern}${subscript}${superscript})`,
    "g",
  );
};

const LATEX_REGEX = buildLatexRegex();

// ===== نرمال‌سازی ارقام فارسی/عربی به لاتین =====
// KaTeX در math mode ارقام فارسی را نمی‌شناسد؛ پس باید قبل از رندر لاتین شوند.
const toLatinDigits = (text) =>
  text
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

// ===== پیش‌پردازش: پوشاندن LaTeXهای خام با $...$ =====
// ترتیب پردازش هر بخش متنی:
//   ۱) نرمال‌سازی ارقام فارسی → لاتین (حتی داخل $...$ موجود)
//   ۲) جدا کردن بخش‌های ریاضی موجود (تا دوباره wrap نشوند)
//   ۳) wrap کردن LaTeXهای خام در بخش‌های غیرریاضی
const MATH_DELIM_REGEX =
  /(\$\$[\s\S]*?\$\$|\$[^$]*?\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\])/;

const wrapRawLatex = (html) => {
  if (!html) return "";

  const htmlParts = html.split(/(<[^>]+>)/g);

  return htmlParts
    .map((part) => {
      if (part.startsWith("<")) return part;

      // ۱) ارقام را یکدست لاتین کن (هم در متن، هم داخل $...$ موجود)
      const normalized = toLatinDigits(part);

      // ۲) بخش‌های ریاضی موجود را جدا کن (odd index = math)
      const segments = normalized.split(MATH_DELIM_REGEX);

      return segments
        .map((seg, i) => {
          if (i % 2 === 1) return seg; // ریاضی موجود → دست نزن
          // ۳) LaTeX خام را بپیچ
          return seg.replace(LATEX_REGEX, (m) => `$${m}$`);
        })
        .join("");
    })
    .join("");
};

// ===== تبدیل اعداد انگلیسی به فارسی در خروجی نهایی =====
const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

const toPersianDigits = (text) =>
  text.replace(/[0-9]/g, (d) => persianDigits[Number(d)]);

// ===== تبدیل ارقام در text nodeها (بدون آسیب به MathML) =====
const convertDigitsInTextNodes = (element) => {
  if (element.nodeType === Node.TEXT_NODE) {
    element.textContent = toPersianDigits(element.textContent);
    return;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      // از MathML (مخفی) صرف‌نظر کن؛ خروجی دیداری katex-html را تبدیل می‌کنیم
      let parent = node.parentNode;
      while (parent && parent !== element) {
        if (
          parent.tagName === "MATH" ||
          (parent.classList && parent.classList.contains("katex-mathml"))
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        parent = parent.parentNode;
      }
      return /[0-9]/.test(node.textContent)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const textNodes = [];
  let currentNode;
  while ((currentNode = walker.nextNode())) {
    textNodes.push(currentNode);
  }

  textNodes.forEach((node) => {
    node.textContent = toPersianDigits(node.textContent);
  });
};

// ===== کامپوننت اصلی =====
const MathContent = ({ html, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    // مرحله ۱: نرمال‌سازی ارقام + پوشاندن LaTeXهای خام
    element.innerHTML = wrapRawLatex(html || "");

    let cancelled = false;

    const renderFormulas = async () => {
      try {
        const { default: renderMathInElement } =
          await import("katex/contrib/auto-render");

        if (cancelled) return;

        // مرحله ۲: رندر KaTeX (ارقام ورودی حالا لاتین‌اند)
        renderMathInElement(element, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true },
          ],
          throwOnError: false,
          ignoredTags: [
            "script",
            "noscript",
            "style",
            "textarea",
            "pre",
            "code",
          ],
        });

        if (cancelled) return;

        // مرحله ۳: ارقام خروجی دیداری را فارسی کن
        convertDigitsInTextNodes(element);
      } catch (err) {
        console.warn("خطا در رندر فرمول‌های ریاضی:", err);
      }
    };

    renderFormulas();

    return () => {
      cancelled = true;
    };
  }, [html]);

  return <div ref={ref} className={className} />;
};

export default MathContent;
